import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Mirrors App\Http\Controllers\Api\NotificationController. */
export interface AppNotification {
  id: string;
  kind: 'certification' | 'sign_on' | 'bill' | 'invitation' | 'reminder' | 'expiring' | 'general';
  title: string;
  body: string;
  url: string | null;
  /** How many events are folded into this one. */
  count: number;
  read: boolean;
  created_at: string | null;
}

export interface NotificationPage {
  data: AppNotification[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    unread: number;
    /** Unread items that are asking something of this person, not telling them. */
    needs_you: number;
  };
}

const KEY = ['notifications'] as const;

export function useNotifications({ filter = 'all', page = 1, perPage = 15, poll = false }: { filter?: 'all' | 'unread' | 'action'; page?: number; perPage?: number; poll?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, filter, page, perPage],
    queryFn: () =>
      api.paginated<AppNotification>('/notifications', { query: { filter, page, per_page: perPage } }) as unknown as Promise<NotificationPage>,
    // The bell checks every minute and when the tab comes back into focus.
    refetchInterval: poll ? 60_000 : false,
    refetchOnWindowFocus: poll,
  });
}

function useChange<TVariables>(fn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }) });
}

export const useMarkRead = () => useChange((id: string) => api.post(`/notifications/${id}/read`));
export const useMarkUnread = () => useChange((id: string) => api.post(`/notifications/${id}/unread`));
export const useMarkAllRead = () => useChange((_: void) => api.post('/notifications/read-all'));
export const useRemoveNotification = () => useChange((id: string) => api.delete(`/notifications/${id}`));

/** "Just now", "5m", "3h", "Yesterday", "2d", then the date. */
export function timeAgo(value: string | null): string {
  if (!value) return '';
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 172_800) return 'Yesterday';
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
